/**
 * 🕘 **한국 시각으로 보이고 한국 시각으로 저장한다 — 기기 시간대와 상관없이** (2026-10-16).
 *
 * 행사·랠리·공지 날짜는 `+09:00` 을 붙여 저장하는데, 입력 칸에 다시 넣을 때는 **그 컴퓨터의 시간대**(`getHours()`)로
 * 풀었다. 한국 시간대 PC 면 같은 값이 돌아오지만, 시간대가 다른 PC(해외 출장·UTC 로 잡힌 노트북·원격 PC)에서는
 * 「10:00」 행사가 「01:00」 으로 보이고, **다른 칸만 고쳐 저장해도 9시간씩 당겨졌다**(공지는 하루씩). 오류는 하나도 없다.
 * 세 화면이 같은 함수를 따로 들고 있었다 — 여기 한 곳에서 UTC 셈으로 푼다.
 */
const KST_MS = 9 * 60 * 60 * 1000;
const p2 = (n: number) => String(n).padStart(2, "0");

/** 이 순간이 한국에서 몇 년 몇 월 며칠 몇 시인가(UTC 셈 — 기기 시간대를 안 탄다) */
function kstParts(ms: number) {
  const d = new Date(ms + KST_MS);
  return { y: d.getUTCFullYear(), m: d.getUTCMonth() + 1, d: d.getUTCDate(), h: d.getUTCHours(), mi: d.getUTCMinutes() };
}

/** ISO → `<input type="datetime-local">` 값(한국 시각). 못 읽으면 빈 값 */
export function kstDateTimeInput(iso?: string): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return "";
  const k = kstParts(t);
  return `${k.y}-${p2(k.m)}-${p2(k.d)}T${p2(k.h)}:${p2(k.mi)}`;
}

/** `datetime-local` 값 → 한국 시각을 밝힌 ISO */
export function isoFromKstDateTime(v: string): string | undefined {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v) ? `${v}:00+09:00` : undefined;
}

/** ISO → `<input type="date">` 값(한국 날짜) */
export function kstDateInput(iso?: string): string {
  return kstDateTimeInput(iso).slice(0, 10);
}

/** `date` 값 → 그날 한국 아침 9시(공지 날짜 — 예전부터 이 모양으로 저장했다) */
export function isoFromKstDate(v: string): string | undefined {
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T09:00:00+09:00` : undefined;
}

/** 오늘(한국) 아침 9시 ISO — 새 공지의 기본 날짜 */
export function kstTodayIso(nowMs: number): string {
  return `${kstDateTimeInput(new Date(nowMs).toISOString()).slice(0, 10)}T09:00:00+09:00`;
}

/** 짧게 「10/31 18:00」(한국 시각) — 못 읽으면 「-」 */
export function kstShort(iso?: string): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return "-";
  const k = kstParts(t);
  return `${k.m}/${k.d} ${p2(k.h)}:${p2(k.mi)}`;
}

/**
 * 📅 **아이·보호자 화면의 날짜·시각 글자 — 한국 시각으로** (2026-10-16).
 * 행사 무대·마을 소식·행사 배너가 `toLocaleString("ko-KR", …)` 를 시간대 없이 불러 **기기 시간대**로 그렸다 —
 * 초기 설정을 안 한 공용 태블릿(UTC)에서 「10월 31일 오전 10시」 행사가 「오전 1:00」 으로 보였다. 행사는 한국에서 열린다.
 * 옛 브라우저가 Intl 시간대를 모르면 던진다 — 그때는 빈 글자(부르는 쪽이 날짜 줄을 숨긴다).
 */
export function formatKst(iso: string | undefined, opts: Intl.DateTimeFormatOptions): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return "";
  try {
    return new Date(t).toLocaleString("ko-KR", { ...opts, timeZone: "Asia/Seoul" });
  } catch {
    return ""; // 의도된 경계: 시간대를 모르는 옛 브라우저 — 틀린 시각을 보이느니 안 보인다
  }
}
